// API Configuration
// Environment-based API URLs

interface Environment {
  API_URL: string;
  DEBUG: boolean;
}

const environments: Record<string, Environment> = {
  development: {
    API_URL: 'http://localhost:5050/api',
    DEBUG: true
  },
  staging: {
    API_URL: 'https://test.kitkeeper.co.uk/api',
    DEBUG: true
  },
  production: {
    API_URL: 'https://book.kitkeeper.co.uk/api',
    DEBUG: false
  }
};

// Determine current environment
const getEnvironment = (): string => {
  // Check for React environment variable
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  
    // Check if running on localhost or local IP
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
        return 'development';
      }
      if (hostname.includes('test.kitkeeper') || hostname.includes('test-logistics.kitkeeper')) {
        return 'staging';
      }
      if (hostname.includes('book.kitkeeper') || hostname.includes('logistics.kitkeeper')) {
        return 'production';
      }
    }
  
  return env;
};

// Export configuration
export const CURRENT_ENV = getEnvironment();

const getApiBaseUrl = (): string => {
  // Build-time injection from CI/CD pipeline takes priority
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (CURRENT_ENV === 'development' && typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // If accessing via IP, assume backend is on the same IP at port 5050
    if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
        return `http://${hostname}:5050/api`;
    }
  }
  return environments[CURRENT_ENV]?.API_URL || environments.development.API_URL;
};

export const API_BASE_URL = getApiBaseUrl();
export const IS_DEBUG = environments[CURRENT_ENV]?.DEBUG ?? true;

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  LOGIN: '/logistics/login',
  ME: '/logistics/me',
  
  // Box Operations
  ORDER_BOX: (barcode: string) => `/logistics/orderbox/${barcode}`,
  ORDER_BOX_STATUS: (orderBoxId: number) => `/logistics/orderbox/${orderBoxId}/status`,
  ORDER_BOX_HISTORY: (orderBoxId: number) => `/logistics/orderbox/${orderBoxId}/history`,
  
  // Route Operations
  COURIER_ROUTES: (date: string) => `/logistics/courier/routes/${date}`,
  COURIER_ROUTE_ORDER: (date: string) => `/logistics/courier/routes/${date}/order`,
  WAREHOUSE_INCOMING: (date: string) => `/logistics/warehouse/incoming/${date}`,
  WAREHOUSE_OUTGOING: (date: string) => `/logistics/warehouse/outgoing/${date}`,
  
  // Order Operations
  ORDER_SIGNATURE: (orderId: number) => `/logistics/orders/${orderId}/signature`,
  ORDER_DRIVER: (orderId: number) => `/logistics/orders/${orderId}/driver`,
  ORDER_ETA: (orderId: number) => `/logistics/orders/${orderId}/eta`,
  ORDER_ADD_BOX: (orderId: number) => `/logistics/orders/${orderId}/boxes`,
  ORDER_REMOVE_BOX: (orderId: number, orderBoxId: number) => `/logistics/orders/${orderId}/boxes/${orderBoxId}`,
  /** Returns a PDF stream of labels for the given BIDs (omit `bids` query param to print all). */
  ORDER_LABELS: (orderId: number) => `/logistics/orders/${orderId}/labels`,
  /** Flag an issue (damaged / overweight / repack / other) on an order or specific box. */
  ORDER_ISSUES: (orderId: number) => `/logistics/orders/${orderId}/issues`,
  
  // Reference Data
  STATUSES: '/logistics/statuses',
  WAREHOUSES: '/logistics/warehouses',
  
  // Partner Operations
  PARTNER: (partnerId: number) => `/logistics/partners/${partnerId}`,
  PARTNER_USERS: (partnerId: number) => `/logistics/partners/${partnerId}/users`,
  
  // Sync
  BATCH_SYNC: '/logistics/sync/batch',

  // OCR
  OCR_EXTRACT: '/logistics/ocr/extract',
  OCR_BATCH_SYNC: '/logistics/ocr/batch-sync',

  // Photos
  PHOTO_UPLOAD: '/logistics/photos/upload',

  // Admin Logistics (requires AdminPrincipal)
  ADMIN_COURIERS: '/admin/logistics/couriers',
  ADMIN_WAREHOUSES: '/admin/logistics/warehouses',
  ADMIN_ORDER_ASSIGN: (orderId: number) => `/admin/logistics/orders/${orderId}/assign`,
  ADMIN_ORDER_ASSIGNMENT: (orderId: number) => `/admin/logistics/orders/${orderId}/assignment`,

  // Partner Dashboard
  PARTNER_FORECAST: '/logistics/dashboard/partner/forecast',
  PARTNER_FORECAST_DETAILS: (date: string) => `/logistics/dashboard/partner/forecast/${date}/details`,
  PARTNER_COLLECTIONS: '/logistics/dashboard/partner/collections',
  PARTNER_COLLECTION_DRIVER: (collectionId: number) => `/logistics/dashboard/partner/collections/${collectionId}/driver`,
  PARTNER_RETURNS: '/logistics/dashboard/partner/returns',
  PARTNER_RETURN_DRIVER: (returnId: number) => `/logistics/dashboard/partner/returns/${returnId}/driver`,
  PARTNER_DRIVERS: '/logistics/dashboard/partner/drivers',
  PARTNER_ROUTE_PLAN: '/logistics/dashboard/partner/route-plan',
  PARTNER_ROUTE_ORDER_UPDATE: (date: string, orderId: number) => `/logistics/dashboard/partner/route-plan/${date}/orders/${orderId}`,
  PARTNER_ROUTE_LOCK: (date: string) => `/logistics/dashboard/partner/route-plan/${date}/lock`,
  PARTNER_INVOICES: '/logistics/dashboard/partner/invoices',

  // Warehouse Dashboard
  WAREHOUSE_OVERVIEW: '/logistics/dashboard/warehouse/overview',
  WAREHOUSE_FORECAST: '/logistics/dashboard/warehouse/forecast',
  WAREHOUSE_FORECAST_DETAILS: (date: string) => `/logistics/dashboard/warehouse/forecast/${date}/details`,
  WAREHOUSE_INBOUND: '/logistics/dashboard/warehouse/inbound',
  WAREHOUSE_OUTBOUND: '/logistics/dashboard/warehouse/outbound',
  WAREHOUSE_STORAGE: '/logistics/dashboard/warehouse/storage',
  WAREHOUSE_INVOICES: '/logistics/dashboard/warehouse/invoices',
  WAREHOUSE_INVOICE_LOCK: (month: string) => `/logistics/dashboard/warehouse/invoices/${month}/lock`,
  WAREHOUSE_BOX_HISTORY: '/logistics/dashboard/warehouse/box-history',
} as const;

// Helper to build full URL
export const buildUrl = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`;
};

// Default headers
export const getHeaders = (token?: string): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

const apiConfig = {
  API_BASE_URL,
  API_ENDPOINTS,
  IS_DEBUG,
  CURRENT_ENV,
  buildUrl,
  getHeaders
};

export default apiConfig;
